import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { HardDrive, Loader2 } from 'lucide-react';

export interface DriveFolder {
  id: string;
  name: string;
  path?: string;
  mimeType?: string;
  isFolder?: boolean; // True if folder, false if file
}

export interface DrivePickerButtonProps {
  onFolderSelected: (folder: DriveFolder) => void;
  onPickerCancelled?: () => void; // Optional callback when picker is cancelled
  buttonText?: string;
  disabled?: boolean;
  clientId?: string;
  apiKey?: string;
  appId?: string;
  accessToken?: string | null; // If provided, skip OAuth and use this token
  autoTrigger?: boolean; // If true, automatically trigger picker on mount
}

// Google Picker API types
declare global {
  interface Window {
    gapi: unknown;
    google: {
      picker: {
        PickerBuilder: new () => PickerBuilder;
        DocsView: new (viewId: string) => DocsView;
        ViewId: { FOLDERS: string; DOCS: string };
        Action: { PICKED: string; CANCEL: string };
        Response: { ACTION: string; DOCUMENTS: string };
        Document: { ID: string; NAME: string; MIME_TYPE: string };
      };
      accounts: {
        oauth2: {
          initTokenClient: (config: TokenClientConfig) => TokenClient;
        };
      };
    };
    pickerInited?: boolean;
    gapiInited?: boolean;
  }
}

interface TokenClientConfig {
  client_id: string;
  scope: string;
  callback: (response: TokenResponse) => void;
  error_callback?: () => void;
}

interface TokenResponse {
  access_token?: string;
  error?: string;
}

interface TokenClient {
  requestAccessToken: (options?: { prompt?: string }) => void;
}

interface PickerBuilder {
  setDeveloperKey: (key: string) => PickerBuilder;
  setAppId: (appId: string) => PickerBuilder;
  setOAuthToken: (token: string) => PickerBuilder;
  addView: (view: DocsView) => PickerBuilder;
  setCallback: (callback: (data: PickerCallbackData) => void) => PickerBuilder;
  setTitle: (title: string) => PickerBuilder;
  setSize: (width: number, height: number) => PickerBuilder;
  build: () => Picker;
}

interface DocsView {
  setIncludeFolders: (include: boolean) => DocsView;
  setSelectFolderEnabled: (enabled: boolean) => DocsView;
  setMimeTypes: (mimeTypes: string) => DocsView;
}

interface Picker {
  setVisible: (visible: boolean) => void;
}

interface PickerCallbackData {
  [key: string]: string | PickerDocument[];
}

interface PickerDocument {
  [key: string]: string;
}

export function DrivePickerButton({
  onFolderSelected,
  onPickerCancelled,
  buttonText = 'Select Google Drive Folder',
  disabled = false,
  clientId = import.meta.env.VITE_GOOGLE_DRIVE_CLIENT_ID,
  apiKey = import.meta.env.VITE_GOOGLE_DRIVE_API_KEY,
  appId = import.meta.env.VITE_GOOGLE_DRIVE_APP_ID,
  accessToken = null,
  autoTrigger = false,
}: DrivePickerButtonProps) {
  const [loading, setLoading] = useState(false);
  const [gapiReady, setGapiReady] = useState(false);
  const [gisReady, setGisReady] = useState(false);
  const autoTriggeredRef = useRef(false); // Prevent multiple auto-triggers

  // Callback after Google Identity Services loaded
  const gapiLoaded = () => {
    if (window.gapi && typeof window.gapi === 'object' && 'load' in window.gapi) {
      (window.gapi as { load: (api: string, callback: () => void) => void }).load('picker', () => {
        window.pickerInited = true;
        setGapiReady(true);
      });
    }
  };

  const gisLoaded = () => {
    window.gapiInited = true;
    setGisReady(true);
  };

  // Load Google API scripts
  useEffect(() => {
    // Load GAPI script for Picker
    if (!document.getElementById('gapi-script')) {
      const gapiScript = document.createElement('script');
      gapiScript.id = 'gapi-script';
      gapiScript.src = 'https://apis.google.com/js/api.js';
      gapiScript.async = true;
      gapiScript.defer = true;
      gapiScript.onload = gapiLoaded;
      document.body.appendChild(gapiScript);
    } else if (window.pickerInited) {
      setGapiReady(true);
    }

    // Load GSI script (Google Identity Services) for OAuth
    if (!document.getElementById('gsi-script')) {
      const gsiScript = document.createElement('script');
      gsiScript.id = 'gsi-script';
      gsiScript.src = 'https://accounts.google.com/gsi/client';
      gsiScript.async = true;
      gsiScript.defer = true;
      gsiScript.onload = gisLoaded;
      document.body.appendChild(gsiScript);
    } else if (window.gapiInited) {
      setGisReady(true);
    }
  }, []);

  // Auto-trigger picker when ready if autoTrigger is true
  useEffect(() => {
    if (autoTrigger && gapiReady && accessToken && !loading && !autoTriggeredRef.current) {
      autoTriggeredRef.current = true; // Mark as triggered
      handleAuthClick();
    }
  }, [autoTrigger, gapiReady, accessToken, loading]);

  // Handle button click
  const handleAuthClick = () => {
    console.log('DrivePickerButton clicked', {
      apiKey: apiKey ? 'present' : 'missing',
      gapiReady,
      gisReady,
      accessToken: accessToken ? 'present' : 'missing',
    });

    if (!apiKey) {
      console.error('Google Drive API key not configured');
      alert('Google Drive integration is not configured. Please contact your administrator.');
      return;
    }

    if (!gapiReady) {
      alert('Google APIs are still loading. Please wait a moment and try again.');
      return;
    }

    // If accessToken is provided from OAuth flow, use it directly
    if (accessToken) {
      console.log('Using provided access token for Picker');
      setLoading(true);
      createPicker(accessToken);
      return;
    }

    // Otherwise, request a new token (fallback for standalone usage)
    if (!clientId || !gisReady) {
      alert('OAuth credentials not configured or Google Identity Services not ready.');
      return;
    }

    setLoading(true);

    // Create OAuth token client for Picker access
    // Important: This gets a short-lived token for Picker API only (doesn't trigger redirect)
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'https://www.googleapis.com/auth/drive.readonly',
      callback: (response: TokenResponse) => {
        if (response.error) {
          console.error('OAuth error:', response);
          alert('Failed to authenticate with Google Drive. Please try again.');
          setLoading(false);
          return;
        }

        if (response.access_token) {
          const token = response.access_token;
          // Small delay to allow OAuth popup to close gracefully before opening Picker
          setTimeout(() => {
            createPicker(token);
          }, 300);
        } else {
          // No token received (user closed popup)
          setLoading(false);
        }
      },
      error_callback: () => {
        // User closed OAuth popup or error occurred
        console.log('OAuth cancelled or failed');
        setLoading(false);
      },
    });

    // Request access token using implicit flow (no redirect, popup only)
    // This will:
    // 1. If already consented: return token immediately in callback
    // 2. If not consented: show consent screen in popup, return token in callback
    // 3. Never redirects the main window
    // Note: Empty prompt string allows reusing existing consent (faster, better UX)
    tokenClient.requestAccessToken({ prompt: '' });
  };

  // Create and show Google Picker
  const createPicker = (token: string) => {
    console.log('Creating Google Picker with token');

    if (!window.google || !window.google.picker) {
      console.error('Google Picker API not loaded');
      alert('Google Picker API is not loaded. Please refresh the page.');
      setLoading(false);
      return;
    }

    try {
      // Create folder view
      const folderView = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)
        .setMimeTypes('application/vnd.google-apps.folder');

      console.log('Building picker...');

      // Build and show picker
      const picker = new window.google.picker.PickerBuilder()
        .setDeveloperKey(apiKey || '')
        .setAppId(appId || clientId || '')
        .setOAuthToken(token)
        .addView(folderView)
        .addView(new window.google.picker.DocsView(window.google.picker.ViewId.DOCS))
        .setCallback(pickerCallback)
        .setTitle('Select a Google Drive Folder or File')
        .setSize(800, 600)
        .build();

      console.log('Showing picker...');
      picker.setVisible(true);
      setLoading(false);
    } catch (error) {
      console.error('Error creating picker:', error);
      alert('Failed to open Google Picker. Please try again.');
      setLoading(false);
    }
  };

  // Handle picker callback
  const pickerCallback = (data: PickerCallbackData) => {
    const action = data[window.google.picker.Response.ACTION] as string;

    if (action === window.google.picker.Action.PICKED) {
      const docs = data[window.google.picker.Response.DOCUMENTS] as PickerDocument[];
      const doc = docs[0];

      const mimeType = doc[window.google.picker.Document.MIME_TYPE];
      const isFolder = mimeType === 'application/vnd.google-apps.folder';

      onFolderSelected({
        id: doc[window.google.picker.Document.ID],
        name: doc[window.google.picker.Document.NAME],
        mimeType: mimeType,
        path: doc[window.google.picker.Document.NAME],
        isFolder: isFolder,
      });

      // Reset loading state after selection
      setLoading(false);
    } else if (action === window.google.picker.Action.CANCEL) {
      // User cancelled picker - notify parent via callback
      console.log('Picker cancelled by user');
      setLoading(false);

      // Call the optional cancellation callback
      if (onPickerCancelled) {
        onPickerCancelled();
      }
    }
  };

  // When autoTrigger is enabled, don't render the button - it's auto-triggered by useEffect
  if (autoTrigger) {
    return null;
  }

  return (
    <Button onClick={handleAuthClick} disabled={disabled || loading} className="gap-2">
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading Picker...
        </>
      ) : (
        <>
          <HardDrive className="h-4 w-4" />
          {buttonText}
        </>
      )}
    </Button>
  );
}
